import React, { useEffect, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tooltip,
    Alert,
    CircularProgress,
} from '@mui/material';
import { fetchYieldMatrix2D } from '../../http/api';
import { useAuth } from "../../Context/AuthProvider";
import { useNavigate } from 'react-router-dom';
import { applyInventoryFilters } from '../../Utils/filterUtils';
import { useBondCharacteristicStore } from '../../Store/bondCharacteristicStore';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { tokens, withAlpha } from '../../Common/designTokens'; // added
dayjs.extend(utc);

const YieldMatrixTable = ({ onLastUpdated, moodyRatings = [], yieldMetric = 'max' }) => {
    const { token } = useAuth();
    const [matrixData, setMatrixData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { resetBondCharacteristicId } = useBondCharacteristicStore();

    // Converts months to years as a floating value (e.g., 3 -> 0.25, 28 -> 2.33)
    const maturityFromMonthsToYears = (months) => {
        if (typeof months !== "number" || isNaN(months)) return null;
        return +(months / 12).toFixed(2);
    };
    // Parse column header label like "3 months", "1 year", "10 years" -> numeric year value
    const parseMaturityHeaderToYears = (label) => {
        if (!label) return null;
        const lower = label.toLowerCase().trim();
        if (lower.includes('month')) {
            const m = parseInt(lower, 10);
            if (isNaN(m)) return null;
            return maturityFromMonthsToYears(m); // already rounded to 2 decimals
        }
        if (lower.includes('year')) {
            const y = parseInt(lower, 10);
            if (isNaN(y)) return null;
            return y; // integer years
        }
        return null;
    };
    // Build filters from bondType row label and maturity column label
    // Now accepts optional cell to use cell.yieldDaysRange (min/max days) and produce pure UTC date strings
    const buildFilters = (bondTypeLabel, maturityLabel, cell) => {
        if (!bondTypeLabel) return null;
        const normalizedBondTypeLabel = bondTypeLabel.toLowerCase().trim();
        let type = null;
        let rating = null;
        if (normalizedBondTypeLabel.startsWith('tsy') || normalizedBondTypeLabel.startsWith('treas')) {
            type = 'Gov';
        } else if (normalizedBondTypeLabel.startsWith('corp')) {
            type = 'Corp';
            rating = bondTypeLabel.split(' ')[1]; // e.g. AAA
        } else if (normalizedBondTypeLabel.startsWith('muni')) {
            type = 'Muni';
            rating = bondTypeLabel.split(' ')[1];
        }
        if (!type) return null;

        const filters = {
            type,
            // default; may be overwritten below
            maturityMin: '0Y',
            maturityMax: undefined,
        };

        // If the matrix cell contains yieldDaysRange, use it to compute pure date strings:
        // current UTC date + minDays  => maturityMin (YYYY-MM-DD)
        // current UTC date + maxDays  => maturityMax (YYYY-MM-DD)
        try {
            if (cell && cell.yieldDaysRange) {
                let rangeObj = cell.yieldDaysRange;
                if (typeof rangeObj === 'string') {
                    rangeObj = JSON.parse(rangeObj);
                }
                const minDays = Number(rangeObj.min);
                const maxDays = Number(rangeObj.max);
                if (!Number.isNaN(minDays) && !Number.isNaN(maxDays)) {
                    const minDate = dayjs().utc().add(minDays, 'day').format('YYYY-MM-DD');
                    const maxDate = dayjs().utc().add(maxDays, 'day').format('YYYY-MM-DD');
                    filters.maturityMin = minDate;
                    filters.maturityMax = maxDate;
                }
            } else {
                // Fallback when no yieldDaysRange: keep previous behaviour using maturity header parsing
                const yearVal = parseMaturityHeaderToYears(maturityLabel);
                if (yearVal !== null) {
                    const maturityMax = `${yearVal % 1 === 0 ? yearVal : yearVal.toFixed(2)}Y`;
                    filters.maturityMin = '0Y';
                    filters.maturityMax = maturityMax;
                } else {
                    // if neither available, leave as-is (caller may ignore)
                    filters.maturityMin = '0Y';
                }
            }

            // NEW: If the matrix cell contains explicit rating info, use it directly.
            // The API may send yieldRatingRange as a JSON string or object, e.g.
            // {"maxSp":"AAA","minSp":"AAA","maxMoody":"Aaa","minMoody":"Aaa"}
            if (cell && cell.yieldRatingRange) {
                let ratingObj = cell.yieldRatingRange;
                if (typeof ratingObj === 'string') {
                    ratingObj = JSON.parse(ratingObj);
                }
                // Prefer explicit values from the cell when present
                if (ratingObj.minSp) filters.minRatingSP = ratingObj.minSp;
                if (ratingObj.maxSp) filters.maxRatingSP = ratingObj.maxSp;
                if (ratingObj.minMoody) filters.minRatingMoody = ratingObj.minMoody;
                if (ratingObj.maxMoody) filters.maxRatingMoody = ratingObj.maxMoody;
            } else {
                // Fallback to bondTypeLabel-derived rating if no explicit rating in the cell
                if (rating) {
                    filters.minRatingSP = rating;
                    filters.maxRatingSP = "AAA";
                    // Moody's logic: find case-insensitive match, use exact Moody's value if found
                    const moodyMatch = moodyRatings.find(
                        (r) => r.rating && r.rating.toLowerCase() === rating.toLowerCase()
                    );
                    if (moodyMatch) {
                        filters.minRatingMoody = moodyMatch.rating;
                        filters.maxRatingMoody = "Aaa";
                    }
                }
            }
        } catch (e) {
            // parsing failed -> fallback to original behavior
            const yearVal = parseMaturityHeaderToYears(maturityLabel);
            if (yearVal !== null) {
                const maturityMax = `${yearVal % 1 === 0 ? yearVal : yearVal.toFixed(2)}Y`;
                filters.maturityMin = '0Y';
                filters.maturityMax = maturityMax;
            }
            // attempt fallback rating assignment from bondTypeLabel
            if (rating) {
                filters.minRatingSP = rating;
                filters.maxRatingSP = "AAA";
                const moodyMatch = moodyRatings.find(
                    (r) => r.rating && r.rating.toLowerCase() === rating.toLowerCase()
                );
                if (moodyMatch) {
                    filters.minRatingMoody = moodyMatch.rating;
                    filters.maxRatingMoody = "Aaa";
                }
            }
        }

        return filters;
    };
    const handleCellClick = (bondTypeLabel, maturityLabel, cell, hasData) => {
        if (!hasData) return;
        const filters = buildFilters(bondTypeLabel, maturityLabel, cell);
        if (!filters) return;
        // Reset characteristic ID when yield table cell is clicked
        resetBondCharacteristicId();
        // Mark source as yieldtable so downstream uses pure dates and special behavior
        applyInventoryFilters(filters, navigate, 'yieldtable'); // navigates to /inventory
    };

    useEffect(() => {  
        const controller = new AbortController();  
        setLoading(true);  
        setError(null);  
        fetchYieldMatrix2D(token, { signal: controller.signal })  
            .then((data) => {  
                setMatrixData(data);  
                setLoading(false);  
                if (onLastUpdated && data && data.lastUpdatedAt) {  
                    onLastUpdated(data.lastUpdatedAt);  
                }  
            })  
            .catch((err) => {  
                if (err.name !== 'AbortError') {  
                    setError(err.message || "Failed to load yield matrix");  
                    setLoading(false);  
                }  
            });  
        return () => controller.abort();  
    }, [token, onLastUpdated]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!matrixData) {
        return null;
    }

    const { maturities = [], maturityRanges = [], bondTypes = [], matrix = [] } = matrixData;
    const maturityRangeByLabel = new Map(
        maturityRanges
            .filter((range) => range?.maturity)
            .map((range) => [range.maturity, range])
    );

    return (
        <Box sx={{ padding: "16px", height: "calc(100vh - 160px)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <TableContainer
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    border: `2px solid ${tokens.colors.bodyBorderColor50}`, // token-based border
                    borderRadius: '8px',
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    backgroundColor: tokens.colors.defaultGridRowColor,
                                    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                                     minWidth: 80,
                                     fontWeight: 700,
                                     textAlign: 'center',
                                    color: tokens.colors.fontColour,
                                     position: 'sticky',
                                     left: 0,
                                     zIndex: 3,
                                    borderRight: `1.6px solid ${tokens.colors.bodyBorderColor50}`,
                                    borderBottom: `1.6px solid ${tokens.colors.bodyBorderColor50}`,
                                }}
                            >
                            </TableCell>
                            {maturities.map((mat) => {
                                const range = maturityRangeByLabel.get(mat);
                                const hasRange = range?.min !== null
                                    && range?.min !== undefined
                                    && range?.max !== null
                                    && range?.max !== undefined
                                    && Number.isFinite(Number(range.min))
                                    && Number.isFinite(Number(range.max));

                                return (
                                <TableCell
                                    key={mat}
                                    sx={{
                                        backgroundColor: tokens.colors.defaultGridRowColor,
                                        border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                                        minWidth: 80,
                                        fontWeight: 700,
                                        textAlign: 'center',
                                        color: tokens.colors.fontColour,
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        borderBottom: `1.6px solid ${tokens.colors.bodyBorderColor50}`,
                                    }}
                                >
                                    <Tooltip title={hasRange ? `${mat} (${range.min} - ${range.max})` : mat}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'inherit', lineHeight: 1.2 }}>
                                                {mat}
                                            </Typography>
                                            {hasRange && (
                                                <Typography variant="caption" sx={{ display: 'block', color: 'inherit', lineHeight: 1.2, fontSize: '11px' }}>
                                                    ({range.min} - {range.max}) days
                                                </Typography>
                                            )}
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bondTypes.map((bondType, rowIdx) => (
                            <TableRow key={bondType}>
                                <TableCell
                                    sx={{
                                        backgroundColor: tokens.colors.defaultGridRowColor,
                                        border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                                        fontWeight: 700,
                                        textAlign: 'left',
                                        color: tokens.colors.fontColour,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1,
                                        minWidth: 80,
                                        borderRight: `1.6px solid ${tokens.colors.bodyBorderColor50}`,
                                    }}
                                >
                                    <Tooltip title={bondType}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
                                            {bondType}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                {maturities.map((mat, colIdx) => {
                                    const cell = matrix[rowIdx]?.[colIdx];

                                    // Determine which yield field to use based on prop
                                    const yieldField = yieldMetric === 'median' ? 'medianYield' : 'maxYield';

                                    // Define emptiness checks:
                                    // - yields: null, undefined, or empty string are empty; numeric 0 counts as a value
                                    // - counts: treat 0 as empty (existing behavior)
                                    const isYieldEmpty = (v) => v === null || v === undefined || v === "";
                                    const isCountEmpty = (v) => v === null || v === undefined || v === "" || v === 0;

                                    // Use specialized emptiness checks
                                    const yieldEmpty = !cell || isYieldEmpty(cell[yieldField]);
                                    const noOfBondsEmpty = !cell || isCountEmpty(cell.noOfBonds);

                                    const hasAnyValue = !yieldEmpty || !noOfBondsEmpty;

                                    // Prepare yield and bond display labels.
                                    // Only append '%' when a numeric yield exists
                                    const displayYieldRaw = yieldEmpty ? null : Number(cell[yieldField]).toFixed(2);
                                    const displayYield = displayYieldRaw === null ? '---' : displayYieldRaw;
                                    let displayNoOfBonds = noOfBondsEmpty ? "---" : Number(cell.noOfBonds).toLocaleString();

                                    const hasData = hasAnyValue;

                                    // If both are empty, show single "---", else show yield and count on separate lines
                                    const showSingleDash = yieldEmpty && noOfBondsEmpty;

                                    // compute hover color only when cell has data
                                    const hoverTint = hasData ? withAlpha(tokens.palette.primary, 0.22) : undefined;

                                    return (
                                        <TableCell
                                            key={`${bondType}-${colIdx}`}
                                            onClick={hasData ? () => handleCellClick(bondType, mat, cell, hasData) : undefined}
                                            sx={{
                                                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                                                padding: '4px',
                                                backgroundColor: tokens.colors.bodyBackgroundColor,
                                                cursor: hasData ? 'pointer' : 'default',
                                                // apply tint to the cell background (redundant)
                                                '&:hover': hasData && hoverTint ? { backgroundColor: hoverTint } : undefined,
                                                // ensure the visible inner element also gets tinted (higher specificity)
                                                '&:hover .ym-inner': hasData && hoverTint ? { backgroundColor: hoverTint } : undefined,
                                            }}
                                        >
                                            <Box
                                                className="ym-inner"
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minHeight: '32px',
                                                    padding: '4px 8px',
                                                    backgroundColor: 'transparent',
                                                    borderRadius: '8px',
                                                    transition: 'background-color 120ms ease-in-out',
                                                }}
                                            >
                                                 {showSingleDash ? (
                                                     <Typography
                                                         variant="body2"
                                                         sx={{
                                                             color: tokens.colors.fontColour,
                                                             fontSize: '0.875rem',
                                                             fontWeight: 500,
                                                             textAlign: 'center',
                                                             lineHeight: 1.2,
                                                         }}
                                                     >
                                                         ---
                                                     </Typography>
                                                 ) : (
                                                     <>
                                                         <Typography
                                                             variant="body2"
                                                             sx={{
                                                                 color: tokens.colors.fontColour,
                                                                 fontSize: '0.875rem',
                                                                 fontWeight: 500,
                                                                 textAlign: 'center',
                                                                 lineHeight: 1.2,
                                                             }}
                                                         >
                                                             {displayYield}{!yieldEmpty ? '%' : ''}
                                                         </Typography>
                                                         <Typography
                                                             variant="body2"
                                                             sx={{
                                                                 color: tokens.colors.fontColour85,
                                                                 fontSize: '0.8rem',
                                                                 fontWeight: 400,
                                                                 textAlign: 'center',
                                                                 opacity: 0.8,
                                                                 lineHeight: 1.2,
                                                             }}
                                                         >
                                                             ({displayNoOfBonds})
                                                         </Typography>
                                                     </>
                                                 )}
                                             </Box>
                                         </TableCell>
                                     );
                                 })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default YieldMatrixTable;
